import { lazy, ReactElement } from 'react';
import { Control } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import RadioButtonGroup, { type RadioButtonOptionProps } from '../RadioButtonGroup';
import { isProductSyncActive } from 'src/helpers/isProductSyncActive/isProductSyncActive';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import { BundleIcon, SaladIcon } from '@weareopenr/component-library';

export interface IMenuItemTypeProps {
  control: Control<any>;
  onEdit?: boolean;
}

const StandardIcon = () => <SaladIcon size="xlarge" />;
const BundleIconComponent = () => <BundleIcon size="xlarge" />;
const ModifierIcon = lazy(() => import('src/assets/images/Modifier'));
const NonFoodIcon = lazy(() => import('src/assets/images/NonFood'));

const standardOption: RadioButtonOptionProps = {
  option: 'STANDARD',
  label: 'Standard',
  LogoComponent: StandardIcon,
  toolTipText: 'A typical product on the menu, e.g. Burger',
};

const modifierOption: RadioButtonOptionProps = {
  option: 'MODIFIER',
  label: 'Modifier',
  LogoComponent: ModifierIcon,
  toolTipText: 'Enhancements, modifications or additions to standard products, e.g. Ketchup',
};

const bundleOption: RadioButtonOptionProps = {
  option: 'BUNDLE',
  label: 'Bundle',
  LogoComponent: BundleIconComponent,
  toolTipText: 'A curated group of products at a fixed price, e.g. Burger Meal Deal',
};

const nonFoodOption: RadioButtonOptionProps = {
  option: 'NON-FOOD',
  label: 'Non-Food',
  LogoComponent: NonFoodIcon,
  toolTipText: 'Non-consumable products that can be used as a standard product or as a modifier, e.g. Cutlery',
};

const MenuItemType = ({ control, onEdit }: IMenuItemTypeProps): ReactElement => {
  const { menuId } = useParams<{ menuId: string }>();
  const { data: externalSystems } = useExternalSystems();
  const isAssureSyncActive = isProductSyncActive(externalSystems);

  let radioOptionsSelection = [standardOption, modifierOption, bundleOption, nonFoodOption];

  if (isAssureSyncActive) {
    radioOptionsSelection = [bundleOption, nonFoodOption];
  }

  if (!isAssureSyncActive && menuId) {
    radioOptionsSelection = [standardOption, bundleOption, nonFoodOption];
  }

  if (onEdit) {
    radioOptionsSelection = [standardOption, modifierOption];
  }

  return <RadioButtonGroup radioButtonGroupName="type" control={control} radioOptions={radioOptionsSelection} />;
};

export default MenuItemType;
